import { zodResolver } from '@hookform/resolvers/zod';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import React, { useEffect, useMemo, useRef } from 'react';
import { FormProvider, type SubmitHandler, useForm } from 'react-hook-form';

import MuiFormInput from 'components/Form/MuiFormInput/MuiFormInput';
import { useUserProfile } from 'contexts/UserProfileContext';

import type { EditUserFormValuesT } from '../../declarations';

import validationSchema from './validationSchema';

type UserEditFormPropsT = {
  onSubmit: SubmitHandler<EditUserFormValuesT>;
};

const UserEditForm: React.FC<UserEditFormPropsT> = ({ onSubmit }) => {
  const { userProfile, isUpdatingProfile, isSuccessfullyUpdatedProfile } = useUserProfile();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const defaultValues = useMemo(
    () => ({
      lastname: userProfile?.lastname,
      firstname: userProfile?.firstname,
      phoneNumber: `+${userProfile?.phoneNumber}`,
    }),
    [userProfile]
  );
  const formMethods = useForm<EditUserFormValuesT>({
    resolver: zodResolver(validationSchema),
    mode: 'onChange',
    defaultValues,
  });

  const { handleSubmit, formState, reset, setValue, watch } = formMethods;
  const { isDirty } = formState;
  const buttonShouldBeReset = !isDirty && isSuccessfullyUpdatedProfile;

  const photoFiles = watch('photoFiles');
  const selectedFileName =
    photoFiles instanceof FileList && photoFiles.length > 0 ? photoFiles[0].name : null;

  useEffect(() => {
    if (isSuccessfullyUpdatedProfile) {
      reset(defaultValues, { keepDirty: false });
    }
  }, [isSuccessfullyUpdatedProfile, defaultValues, reset]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const files = e.target.files;
    if (files && files.length > 0) {
      setValue('photoFiles', files, { shouldDirty: true, shouldValidate: true });
    }
  };

  return (
    <Box
      sx={{
        flex: 1,
        mt: { xs: 3, md: 0 },
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 1000 }}>
        <Typography variant="h5" color="text.primary" fontWeight={700}>
          Edit
        </Typography>
        <Box sx={{ mt: 4 }}>
          <FormProvider {...formMethods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Box sx={{ mb: 3.5 }}>
                <MuiFormInput
                  name="firstname"
                  label="First Name"
                  placeholder="First name"
                  required
                />
              </Box>
              <Box sx={{ mb: 3.5 }}>
                <MuiFormInput name="lastname" label="Last Name" placeholder="Last name" required />
              </Box>
              <Box sx={{ mb: 3.5 }}>
                <MuiFormInput
                  name="phoneNumber"
                  label="Mobile Number"
                  placeholder="+1000000000"
                  required
                />
              </Box>

              <Box sx={{ mb: 3.5 }}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg, image/png"
                  onChange={handleFileSelect}
                  hidden
                />
                <Button
                  variant="outlined"
                  startIcon={<AddPhotoAlternateOutlinedIcon />}
                  onClick={(): void => fileInputRef.current?.click()}
                >
                  {selectedFileName ?? 'Add Image'}
                </Button>
                {formState.errors.photoFiles && (
                  <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
                    {formState.errors.photoFiles.message as string}
                  </Typography>
                )}
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ mt: 0.5 }}
                >
                  Must be .png or .jpeg format (max 5MB)
                </Typography>
              </Box>

              <Typography variant="body2" color="error">
                <i>* Indicates required fields</i>
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 4 }}>
                <i>If you want to change email, please connect with your WarrCloud Agent</i>
              </Typography>
              <Box sx={{ position: 'fixed', bottom: 32, right: 32 }}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={isUpdatingProfile || !isDirty}
                  color={buttonShouldBeReset ? 'success' : 'primary'}
                  sx={{ minWidth: 170 }}
                  startIcon={
                    isUpdatingProfile ? <CircularProgress size={18} color="inherit" /> : undefined
                  }
                >
                  {buttonShouldBeReset ? 'Saved successfully' : 'Save changes'}
                </Button>
              </Box>
            </form>
          </FormProvider>
        </Box>
      </Box>
    </Box>
  );
};

export default UserEditForm;
